import { FiUserCheck as UserCheck, FiChevronRight as ChevronRight } from "react-icons/fi";
import { SessionParticipant, SessionWithUsers } from "../../../types";
import { Link } from "react-router-dom";

/**
 * Helper to safely handle missing or deleted users.
 */
function safeUser(user: SessionParticipant | undefined, id: string, role: "COACH" | "LEARNER"): SessionParticipant {
  return (
    user ?? {
      id,
      name: "Deleted User",
      role,
      pictureURL: "/deleted-user.png",
    }
  );
}

/**
 * Card component for displaying a single participant (coach or learner)
 */
function ParticipantCard({
  user,
  joined,
  bgClass,
  href,
}: {
  user: SessionParticipant;
  joined: boolean;
  bgClass: string;
  href?: string;
}) {
  const content = (
    <div
      className={`relative flex items-center justify-between p-4 rounded-lg ${bgClass} transition ring-1 ring-transparent hover:ring-blue-300 focus-within:ring-blue-400`}
    >
      <div className="flex items-center space-x-3">
        <div className="shrink-0">
          <img
            src={user.pictureURL}
            width="50"
            height="50"
            alt={`${user.name} avatar`}
            className="rounded-full object-cover"
          />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900">{user.name}</h4>
          <span className="text-xs text-gray-500">{user.role}</span>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <UserCheck className={`w-4 h-4 ${joined ? "text-green-600" : "text-red-600"}`} />
        <span className={`text-sm ${joined ? "text-green-600" : "text-red-600"}`}>
          {joined ? "Joined" : "Not Joined"}
        </span>
        {href ? <ChevronRight className="w-4 h-4 text-gray-400" /> : null}
      </div>

      {href ? <span className="absolute inset-0 rounded-lg" aria-hidden="true" /> : null}
    </div>
  );

  return href ? (
    <Link to={href} className="block focus:outline-none no-underline" aria-label={`Open ${user.name} info`}>
      {content}
    </Link>
  ) : (
    <div aria-disabled className="opacity-80">
      {content}
    </div>
  );
}

/**
 * Section that lists all participants (coach + learners)
 */
export const ParticipantsSection: React.FC<SessionWithUsers> = ({ session, users }) => {
  // Safely get coach (fallback if missing)
  const coach = safeUser(users[session.coachId], session.coachId, "COACH");
  const coachHref = coach.name === "Deleted User" ? undefined : `/users/${coach.id}`;
  const coachJoined = !!session.isCoachJoined;

  return (
    <div className="mt-4 space-y-4">
      {/* Coach card */}
      <ParticipantCard user={coach} joined={coachJoined} bgClass="bg-blue-50" href={coachHref} />

      {/* Learner cards */}
      {session.learnerIds.map((learnerId) => {
        const learner = safeUser(users[learnerId], learnerId, "LEARNER");
        const isJoined = !session.missedParticipantIds.includes(learner.id);
        const href = learner.name === "Deleted User" ? undefined : `/users/${learner.id}`;

        return <ParticipantCard key={learnerId} user={learner} joined={isJoined} bgClass="bg-green-50" href={href} />;
      })}
    </div>
  );
};
